import React, { useState, useEffect, useContext } from "react";
import "../../CarrierProfile/CarrierProfile.css";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, CircularProgress, FormControl, FormControlLabel, RadioGroup } from "@mui/material";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryRadio from "../../Buttons/PrimaryRadio/PrimaryRadio";
import CarrierAutocomplete from "../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import CarrierMcNumberAutocomplete from "../../Inputs/CarrierMcNumberSearch/CarrierMcNumberAutocomplete";
import CarrierDotNumberAutocomplete from "../../Inputs/CarrierDotNumberAutocomplete/CarrierDotNumberAutocomplete";
import CityAutocomplete from "../../Inputs/CityAutocomplete/CityAutocomplete";
import PrimaryDateTimePicker from "../../Inputs/PrimaryDateTimePicker/PrimaryDateTimePicker";
import DateTimeNowButton from "../../Buttons/DateTimeNowButton/DateTimeNowButton";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import WarningMessage from "../../WarningMessage/WarningMessage";
import CarrierOffersModalSidebar from "./CarrierOffersModalSidebar/CarrierOffersModalSidebar";
import CarrierOffersModalHeader from "./CarrierOffersModalHeader/CarrierOffersModalHeader";
import { parseAndFormatDate, toISOLocal } from "../../../utils/dateHelpers";
import { sortArrayOfDateObjectsDesc } from "../../../utils/sortHelpers";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { getDistance } from "../../../utils/calcHelpers";
import { formatPhoneNumber } from "../../../utils/formatHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { LoadService } from "../../../services/LoadService";
import { ActiveUserContext, AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierOffersModal({ handleTableAction }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  let [searchParams] = useSearchParams();
  const minDate = new Date(0).toISOString();
  const [isLoading, setIsLoading] = useState(false);
  const [load, setLoad] = useState({});
  const [offers, setOffers] = useState([]);
  const [comments, setComments] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [origin, setOrigin] = useState({});
  const [destination, setDestination] = useState({});
  const [originDate, setOriginDate] = useState("");
  const [destinationDate, setDestinationDate] = useState("");
  const [extraStops, setExtraStops] = useState("");
  const [fscPerMile, setFscPerMile] = useState(0);
  const [capacity, setCapacity] = useState(true);
  const [carrierId, setCarrierId] = useState("");
  const [carrier, setCarrier] = useState(null);
  const [carrierValid, setCarrierValid] = useState(false);
  const [city, setCity] = useState({});
  const [cityValid, setCityValid] = useState(false);
  const [expEmptyTime, setExpEmptyTime] = useState(new Date());
  const [expEmptyTimeValid, setExpEmptyTimeValid] = useState(true);
  const [miles, setMiles] = useState(null);
  const [totalRate, setTotalRate] = useState(null);
  const [totalRateValid, setTotalRateValid] = useState(false);
  const [linehaul, setLinehaul] = useState(null);
  const [fuelSurcharge, setFuelSurcharge] = useState(null);
  const [contact, setContact] = useState("");
  const [contacts, setContacts] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [mobilePhone, setMobilePhone] = useState(null);
  const [offerComments, setOfferComments] = useState("");
  const [displaySidebar, setDisplaySidebar] = useState("none");
  const [displayMiniSidebar, setDisplayMiniSidebar] = useState("block");
  const [sidebarBG, setSidebarBG] = useState("black_solid.10");
  const [warningText, setWarningText] = useState("");

  const handleClose = () => {
    window.close();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleSave = () => {
    let payload = {
      offerType: "carrier_offer",
      orderId: load?.id,
      companyId: "TMS",
      amount: totalRate || 0,
      capacity: capacity,
      declineReason: capacity ? null : "2",
      carrierName: carrier != null ? carrier.name : "",
      carrierId: carrier != null ? carrier.id : "",
      comments: offerComments,
      icc_number: carrier != null ? carrier.mcNumber : "",
      dot_number: carrier != null ? carrier.dotNumber : "",
      miles: miles,
      expEmptyTime: expEmptyTime ? toISOLocal(expEmptyTime) : minDate,
      contactName: contact != "" ? contacts.find(({ id }) => id === contact).name : null,
      email: contact != "" ? contacts.find(({ id }) => id === contact).email : null,
      contactPhone: phoneNumber,
      mobilePhone: mobilePhone,
      modifiedDate: new Date(),
      city: city.name,
      cityId: city.id,
      state: city.state_id,
      orderTotalCharge: load?.total_charge,
      movementId: load?.curr_movement_id,
      enteredUserId: activeUser?.userIdMcld,
      approvedStatus: capacity ? null : "7",
    };

    setSubmitDisabled(true);
    setIsLoading(true);
    CarrierService.CreateCarrierOffer(instance, payload).then(() => {
      setIsLoading(false);
      handleClose();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  const handleDisplaySidebar = () => {
    if (displaySidebar == "none") {
      setDisplaySidebar("block");
      setDisplayMiniSidebar("none");
      setSidebarBG("");
    } else {
      setDisplaySidebar("none");
      setDisplayMiniSidebar("block");
      setSidebarBG("black_solid.10");
    }
  };

  const handleCapacityChange = event => {
    setTotalRate("");
    setFuelSurcharge("");
    setLinehaul("");

    if (event.target.value == "false") {
      setCapacity(false);
      setTotalRateValid(true);
      setExpEmptyTimeValid(true);
      setCityValid(true);
    } else {
      setCapacity(true);
      setTotalRateValid(false);
      if (!expEmptyTime) setExpEmptyTimeValid(false);
      if (!city.id) setCityValid(false);
    }
  };

  const handleCityChange = c => {
    if (c) {
      setCity(c);
      setCityValid(true);
      setMiles(getDistance(c.latitude, c.longitude, origin.latitude, origin.longitude));
    }
    else {
      setCity({});
      setMiles(null);
      setCityValid(!capacity);
    }
  };

  const handleExpEmptyTime = date => {
    if (date) {
      setExpEmptyTime(date);
      setExpEmptyTimeValid(true);
    } else {
      setExpEmptyTime(null);
      setExpEmptyTimeValid(!capacity);
    }
  };

  const calculateLinehaulAndFuel = () => {
    const miles = load.miles;
    const fsc = miles * fscPerMile;
    const lh = totalRate - fsc;
    setLinehaul(Math.round((lh + Number.EPSILON) * 100) / 100);
    setFuelSurcharge(Math.round((fsc + Number.EPSILON) * 100) / 100);
  };

  const calculateTotalRate = () => {
    const fsc = parseFloat(fuelSurcharge) || 0;
    const lh = parseFloat(linehaul) || 0;
    setTotalRate(Math.round(((lh + fsc) + Number.EPSILON) * 100) / 100);
  };

  const handleContactChange = value => {
    if (value) {
      setContact(value);

      let contact = contacts.find(({ id }) => id === value);
      setPhoneNumber(formatPhoneNumber(contact.phone));
      setMobilePhone(formatPhoneNumber(contact.mobile));
    }
  };

  useEffect(() => {
    if (totalRate <= 0 && capacity) {
      setTotalRateValid(false);
    } else {
      setTotalRateValid(true);
    }

    if (totalRate > 0 && load.miles && fscPerMile) {
      calculateLinehaulAndFuel();
    }
  }, [totalRate, fscPerMile]);

  useEffect(() => {
    setSubmitDisabled(
      !carrierValid ||
      !cityValid ||
      !totalRateValid ||
      !expEmptyTimeValid ||
      !!warningText
    );
  }, [
    carrierValid,
    cityValid,
    totalRateValid,
    expEmptyTimeValid,
    warningText
  ]);

  useEffect(() => {
    setCarrierValid(!!carrier);

    if (carrier) {
      CarrierService.GetCarrierContacts(instance, carrier.id).then(res => {
        setContacts(res);
        if (res.length) {
          setContact(res[0].id);
          setPhoneNumber(formatPhoneNumber(res[0].phone));
          setMobilePhone(formatPhoneNumber(res[0].mobile));
        }
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    } else {
      setContact("");
      setPhoneNumber("");
      setMobilePhone("");
      setContacts([]);
    }
  }, [carrier]);

  useEffect(() => {
    if (load?.id) {
      const stops = load.stops;
      if (stops?.length) {
        const originStop = stops[0];
        const destinationStop = stops[stops.length - 1];

        setOrigin(originStop);
        setDestination(destinationStop);
        setOriginDate(parseAndFormatDate(originStop.schedArriveEarly, "MM/dd/yy HH:mm"));
        setDestinationDate(parseAndFormatDate(destinationStop.schedArriveEarly, "MM/dd/yy HH:mm"));
        setExtraStops(stops.length > 2 ? (`+${stops.length - 2} stop(s)`) : "");

        if (carrier?.mcNumber === null && !stops.every(s => s.state === stops[0].state)) {
          setWarningText("ERROR: Offer is not valid. Carrier is missing MC and/or DOT number");
        } else if (carrier?.mcNumber === null && carrier?.dotNumber === null && stops.every(s => s.state === stops[0].state)) {
          setWarningText("ERROR: Offer is not valid. Carrier is missing MC and/or DOT number");
        } else {
          setWarningText("");
        }
      }
    }
  }, [load, carrier]);

  useEffect(() => {
    const loadId = searchParams.get("loadId");
    if (loadId) {
      setIsLoading(true);
      LoadService.GetLoad(instance, loadId).then(res => {
        setLoad(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });

      CarrierService.GetLoadOffers(instance, loadId).then(res => {
        const sortedOffers = sortArrayOfDateObjectsDesc(res.offers.filter(({ amount }) => amount != null && amount > 0), "lastUpdatedDate");
        setOffers(sortedOffers);
      }).catch(error => {
        console.error(error);
        warning(error);
      });

      LoadService.GetLoadComments(instance, loadId, "HC").then(res => {
        setComments(res);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }

    if (searchParams.get("carrierId")) {
      setCarrierId(searchParams.get("carrierId"));
    }

    CarrierService.GetFuelSurcharge(instance).then(res => {
      setFscPerMile(res.surcharge);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, submitDisabled, handleSave)} sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <>
          <CarrierOffersModalHeader
            displaySidebar={displaySidebar}
            displayMiniSidebar={displayMiniSidebar}
            load={load}
            origin={origin}
            originDate={originDate}
            destination={destination}
            destinationDate={destinationDate}
            extraStops={extraStops}
          />
          <Box sx={{ maxHeight: "80vh", overflow: "scroll", display: "flex" }}>
            <Box sx={{ padding: "10px 10px 0 0" }}>
              <Typography variant="h3" color="primary.60">
                Select a Carrier
              </Typography>
              <Box className="Carrier-modal-input-row" sx={{ alignItems: "center" }}>
                <Box sx={{ width: "150px", paddingX: "5px" }}>
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="offer-type-radio-buttons"
                      name="row-radio-buttons-group"
                      value={capacity}
                      onChange={handleCapacityChange}
                    >
                      <FormControlLabel
                        value={true}
                        control={<PrimaryRadio />}
                        label={<Typography variant="subhead2" color="black_solid.60">Cap</Typography>}
                      />
                      <FormControlLabel
                        value={false}
                        control={<PrimaryRadio />}
                        label={<Typography variant="subhead2" color="black_solid.60">No Cap</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
              <Box className="Carrier-modal-input-row">
                <CarrierAutocomplete label="Carrier *" width="400px" carrierId={carrierId} selectedOption={carrier} updateSelectedCarrier={setCarrier} error={!carrier} />
              </Box>
              <Box className="Carrier-modal-input-row">
                <Box>
                  <CarrierMcNumberAutocomplete label="MC # *" width="190px" selectedOption={carrier} updateSelectedCarrier={setCarrier} error={!carrier} />
                </Box>
                <Box sx={{ marginLeft: "20px" }}>
                  <CarrierDotNumberAutocomplete label="DOT # *" width="190px" selectedOption={carrier} updateSelectedCarrier={setCarrier} error={!carrier} />
                </Box>
              </Box>
              <Box className="Carrier-modal-input-row">
                <CityAutocomplete label="Current Location *" width="400px" selectedOption={city} updateSelectedCity={handleCityChange} error={!cityValid} />
              </Box>
              <Box className="Carrier-modal-input-row">
                <Box sx={{ display: "flex" }}>
                  <PrimaryDateTimePicker
                    label="Expected Empty Time *"
                    value={expEmptyTime}
                    handleChange={handleExpEmptyTime}
                    width="170px"
                    error={!expEmptyTimeValid}
                    ampm={false}
                  />
                  <DateTimeNowButton onClick={() => handleExpEmptyTime(new Date)} height="28px" />
                </Box>
                <Box sx={{ marginLeft: "20px" }}>
                  <PrimaryTextField
                    label="Miles to Origin"
                    onChange={setMiles}
                    value={miles}
                    width="180px"
                    isNumber
                    disabled
                  />
                </Box>
              </Box>
              <Box className="Carrier-modal-input-row">
                <Box>
                  <PrimaryTextField
                    label="Total *"
                    onChange={setTotalRate}
                    onBlur={calculateLinehaulAndFuel}
                    value={totalRate}
                    width="120px"
                    isNumber
                    isCurrency
                    disabled={!capacity}
                    error={!totalRateValid}
                  />
                </Box>
                <Box sx={{ marginLeft: "20px" }}>
                  <PrimaryTextField
                    label="Line Haul"
                    onChange={setLinehaul}
                    onBlur={calculateTotalRate}
                    value={linehaul}
                    width="120px"
                    isNumber
                    isCurrency
                    disabled={!capacity}
                  />
                </Box>
                <Box sx={{ marginLeft: "20px" }}>
                  <PrimaryTextField
                    label="FSC"
                    onChange={setFuelSurcharge}
                    onBlur={calculateTotalRate}
                    value={fuelSurcharge}
                    width="120px"
                    isNumber
                    isCurrency
                    disabled={!capacity}
                  />
                  <Box sx={{ display: "flex", justifyContent: "end" }}>
                    <Typography variant="label1" color="black_solid.50">${fscPerMile} per mile</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="Carrier-modal-input-row">
                <PrimarySelect
                  label="Carrier Contact"
                  options={contacts.map((o) => (o))}
                  onChange={o => handleContactChange(o)}
                  value={contact}
                  flexGrow={1}
                  nameKey="name"
                  valueKey="id"
                  disabled={isLoading}
                />
              </Box>
              <Box className="Carrier-modal-input-row">
                <Box>
                  <PrimaryTextField
                    label="Phone Number"
                    onChange={setPhoneNumber}
                    value={phoneNumber}
                    width="190px"
                    isPhone
                  />
                </Box>
                <Box sx={{ marginLeft: "20px" }}>
                  <PrimaryTextField
                    label="Mobile Phone"
                    onChange={setMobilePhone}
                    value={mobilePhone}
                    width="190px"
                    isPhone
                  />
                </Box>
              </Box>
              <Box>
                <PrimaryTextField
                  label="Comments"
                  onChange={setOfferComments}
                  value={offerComments}
                  width="400px"
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", width: "380px", marginTop: "20px" }}>
                <Box>
                  {warningText && !isLoading &&
                    <WarningMessage text={warningText} displayText={warningText} />
                  }
                </Box>
                <Box sx={{ marginLeft: "auto", padding: "5px 10px 5px 5px" }}>
                  <SecondaryButton
                    label="Cancel"
                    onClick={handleCancel}
                    disabled={isLoading}
                  />
                </Box>
                <Box sx={{ padding: "5px 0 5px 5px" }}>
                  <PrimaryButton
                    label="Submit"
                    width="60px"
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={submitDisabled}
                  />
                </Box>
              </Box>
            </Box>
            <CarrierOffersModalSidebar
              displaySidebar={displaySidebar}
              displayMiniSidebar={displayMiniSidebar}
              sidebarBG={sidebarBG}
              handleDisplaySidebar={handleDisplaySidebar}
              load={load}
              offers={offers}
              comments={comments}
              originDate={originDate}
              destinationDate={destinationDate}
              handleTableAction={handleTableAction}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

CarrierOffersModal.propTypes = {
  handleTableAction: PropTypes.func,
};